import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Send,
  Loader2,
  Save,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { DynamicFormRenderer } from '@/components/common/dynamic-form-renderer'
import { MerchantJourneyStepper } from '@/components/common/merchant-journey-stepper'
import { appRoutes } from '@/constants'
import { useCustomerFormConfig, useSubmitSellerRequest } from '../../hooks/use-customer'
import { api, unwrap } from '@/api'
import { toast } from 'sonner'

export function BecomeSellerPage() {
  const navigate = useNavigate()
  const { data: formConfigData, isLoading: isLoadingConfig } = useCustomerFormConfig('SELLER')
  const submitSellerMutation = useSubmitSellerRequest()

  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [remarks, setRemarks] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [existingRequest, setExistingRequest] = useState<any | null>(null)
  const [isDraftSaving, setIsDraftSaving] = useState(false)

  const fields = (formConfigData as any)?.fields || []
  const documents = (formConfigData as any)?.documents || []
  const formVersion = (formConfigData as any)?.version || 1
  const formConfigId = (formConfigData as any)?.id

  // Load existing draft or application on mount
  useEffect(() => {
    api
      .get('/platform/application/draft?type=SELLER')
      .then((res) => {
        const draft = unwrap(res)
        if (draft) {
          setExistingRequest(draft)
          if (draft.applicationData) {
            setFormValues(draft.applicationData)
          }
          if (draft.remarks) {
            setRemarks(draft.remarks)
          }
        }
      })
      .catch(() => {})
  }, [])

  const handleFieldValueChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleSaveDraft = async () => {
    try {
      setIsDraftSaving(true)
      await api.post('/platform/application/save-draft', {
        requestType: 'SELLER',
        formConfigId,
        formVersion,
        formConfigSnapshot: formConfigData,
        applicationData: cleanApplicationData(formValues),
        uploadedDocuments: extractDocuments(formValues),
        remarks,
      })
      toast.success('Application draft saved! You can resume anytime.')
    } catch (error: any) {
      toast.error(error.message || 'Failed to save draft.')
    } finally {
      setIsDraftSaving(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreed) {
      toast.error('Please agree to the seller terms and marketplace policy.')
      return
    }

    const payload = {
      formConfigId,
      formVersion,
      formConfigSnapshot: formConfigData,
      applicationData: cleanApplicationData(formValues),
      uploadedDocuments: extractDocuments(formValues),
      remarks,
    }

    await submitSellerMutation.mutateAsync(payload)
    navigate(appRoutes.customer.approvalHistory)
  }

  // Extracts file-upload entries from formValues → goes into uploadedDocuments
  const extractDocuments = (values: Record<string, any>) => {
    const docs: any[] = []
    Object.entries(values).forEach(([k, v]) => {
      if (v && typeof v === 'object' && v.fileName) {
        docs.push({ ...v, field: v.field || k })
      }
    })
    return docs
  }

  // Strips document object entries from applicationData — keeps only scalar values
  const cleanApplicationData = (values: Record<string, any>) => {
    const clean: Record<string, any> = {}
    Object.entries(values).forEach(([k, v]) => {
      // Skip if value is an object (file upload) or key starts with doc_
      if (v === null || v === undefined) return
      if (typeof v === 'object') return // file/doc objects go to uploadedDocuments
      clean[k] = v
    })
    return clean
  }

  const isRejected = existingRequest?.status === 'REJECTED'

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Merchant Journey Stepper */}
      <MerchantJourneyStepper currentStep={existingRequest?.status === 'PENDING' ? 'PENDING_APPROVAL' : 'SUBMIT_APPLICATION'} />

      {/* Page Header */}
      <SectionHeading
        eyebrow="Merchant Onboarding"
        title="Become a Verified Seller"
        description="List luxury vehicles and genuine auto parts directly on Exotic Autocart. Reach thousands of active automotive buyers across the country."
        align="left"
      />

      {/* Rejection Notice Banner (If Re-submitting) */}
      {isRejected && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-500 p-4 space-y-2">
          <div className="flex items-center gap-2 font-bold text-sm">
            <AlertTriangle className="size-5 shrink-0 text-red-500" />
            <span>Previous Application Rejection & Review Notes</span>
          </div>
          <p className="text-xs text-foreground/90 font-medium">
            {existingRequest?.reviewNotes || existingRequest?.remarks || 'Your previous application required updates. Please edit the form below and resubmit.'}
          </p>
        </Card>
      )}

      {/* Dynamic Application Form */}
      <Card className="border-slate-200 dark:border-primary/40 bg-white dark:bg-card shadow-md">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-border/60 pb-3">
            <div className="flex items-center gap-2">
              <Send className="size-5 text-primary" />
              <h3 className="font-display text-base font-bold text-slate-900 dark:text-foreground">
                {formConfigData?.title || 'Submit Seller Application'}
              </h3>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSaveDraft}
              disabled={isDraftSaving}
              className="gap-1 text-xs font-bold"
            >
              <Save className="size-3.5" /> {isDraftSaving ? 'Saving...' : 'Save Draft'}
            </Button>
          </div>

          {isLoadingConfig ? (
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Loading application form fields...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dynamic Fields Renderer — mirrors admin preview exactly */}
              <DynamicFormRenderer
                fields={fields}
                documents={documents}
                values={formValues}
                onChange={handleFieldValueChange}
              />

              <div className="space-y-1.5 pt-3 border-t border-slate-200 dark:border-border/60">
                <label className="text-xs font-semibold text-slate-600 dark:text-muted-foreground">
                  Additional Application Remarks & Selling Intent (Optional)
                </label>
                <textarea
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Mention what types of cars or spare parts you plan to list..."
                  className="w-full rounded-lg border border-slate-300 dark:border-border/80 bg-white dark:bg-background text-slate-900 dark:text-foreground placeholder:text-slate-400 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <div className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-border/80 bg-slate-50 dark:bg-background/60 p-4">
                <input
                  type="checkbox"
                  id="agree"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 size-4 rounded border-slate-300 dark:border-border text-primary focus:ring-primary"
                />
                <label htmlFor="agree" className="text-xs text-slate-600 dark:text-muted-foreground leading-relaxed cursor-pointer select-none">
                  I agree to the <span className="font-bold text-slate-900 dark:text-foreground">Exotic Autocart Seller Terms of Service</span>. I confirm that all listed items will be authentic, accurately represented, and compliant with automotive commerce policies.
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={submitSellerMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitSellerMutation.isPending || !agreed}
                  className="gap-2 font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow"
                >
                  {submitSellerMutation.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> Submitting...
                    </>
                  ) : isRejected ? (
                    <>
                      <RotateCcw className="size-4" /> Resubmit Application
                    </>
                  ) : (
                    <>
                      <Send className="size-4" /> Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
